import { compact } from 'lodash';

import { MiniTableRow } from '@/components/tables/MiniTable/MiniTable';
import { keyPersonRoleDisplayNames } from '@/modules/entities/details/businessEntities/businessEntities.constants';
import { BusinessEntitySummaryData } from '@/modules/entities/details/businessEntities/BusinessEntityDetailsCard/BusinessEntityDetails.types';
import { getBusinessEntitySummaryProperties } from '@/modules/entities/details/businessEntities/BusinessEntityDetailsCard/BusinessEntityDetails.utils';
import {
  BusinessEntityDetailsCard_KeyPersonFragment,
  EntityDetail_EntityFragment,
} from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { EntityKind, KeyPersonRoleKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import {
  EntityCoverSlideData,
  EntityCoverSlideDataPoint,
  EntityCoverSlideDetailsList,
} from './EntityCoverSlide.types';
import {
  dataPointGetters,
  getTotalDeathBenefit,
  getTrimmedListForEntityCoverSlide,
} from './EntityCoverSlide.utils';

export function getBusinessEntityDetails(
  entity: EntityDetail_EntityFragment,
  entityKind:
    | EntityKind.LlcBusinessEntity
    | EntityKind.SoleProprietorshipBusinessEntity
    | EntityKind.ScorpBusinessEntity
    | EntityKind.CcorpBusinessEntity
    | EntityKind.GpBusinessEntity
    | EntityKind.LpBusinessEntity
): Omit<EntityCoverSlideData, 'title'> {
  let summary: BusinessEntitySummaryData | null = null;

  switch (entityKind) {
    case EntityKind.LlcBusinessEntity:
      summary = getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.LLC,
      });
      break;
    case EntityKind.SoleProprietorshipBusinessEntity:
      summary = getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.SOLE_PROPRIETORSHIP,
      });
      break;
    case EntityKind.ScorpBusinessEntity:
      summary = getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.S_CORP,
      });
      break;
    case EntityKind.CcorpBusinessEntity:
      summary = getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.C_CORP,
      });
      break;
    case EntityKind.GpBusinessEntity:
      summary = getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.GP,
      });
      break;
    case EntityKind.LpBusinessEntity:
      summary = getBusinessEntitySummaryProperties({
        entity,
        entityType: ENTITY_TYPES.LP,
      });
      break;
    default:
      throw new UnreachableError({
        case: entityKind,
        message: `Unhandled business entity kind: ${entityKind}`,
      });
  }
  if (!summary) {
    return {
      dataPoints: [],
      detailsLists: [],
    };
  }

  const dataPoints: EntityCoverSlideDataPoint[] = compact([
    dataPointGetters.getTotalMarketValueDataPoint(entity),
    dataPointGetters.getTaxStatusDataPoint({
      taxStatus: summary.taxStatus ? { value: summary.taxStatus } : undefined,
    }),
    dataPointGetters.getJurisdictionDataPoint(summary),
    dataPointGetters.getTaxIdDataPoint(summary),
    dataPointGetters.getFinCenIDDataPoint(summary),
    dataPointGetters.getEffectiveDateDataPoint(summary),
  ]);
  const detailsLists: EntityCoverSlideDetailsList[] = compact([
    getBusinessEntityKeyPeople(summary),
  ]);

  return {
    dataPoints,
    detailsLists,
    totalDeathBenefit: getTotalDeathBenefit(summary),
  };
}

function getBusinessEntityKeyPeople({
  keyPeople,
}: {
  keyPeople?: BusinessEntityDetailsCard_KeyPersonFragment[] | undefined;
}): EntityCoverSlideDetailsList | null {
  const entries = Object.entries(keyPersonRoleDisplayNames).reduce<
    MiniTableRow[]
  >((acc, [role, label]) => {
    const names = keyPeople
      ?.filter((keyPerson) =>
        keyPerson.roles?.some((r) => r.kind === (role as KeyPersonRoleKind))
      )
      ?.map(
        (keyPerson) =>
          keyPerson.individual?.displayName ??
          keyPerson.entity?.subtype.displayName ??
          keyPerson.organization?.name ??
          ''
      );

    if (names?.length === 0) {
      return acc;
    }

    const joinedNames = names?.join(', ') || '';

    acc.push({
      label,
      value: joinedNames,
    });

    return acc;
  }, []);
  return {
    title: 'Key people',
    entries: getTrimmedListForEntityCoverSlide(entries),
  };
}
